'use client'
import { FC } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText} from "@prismicio/react";
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next' 
import { ResponsivePrismicImage } from '@/components'

/**
 * Props for `Banner`.
 */
export type BannerProps = SliceComponentProps<Content.BannerSlice>;

/**
 * Component for "Banner" Slices.
 */
const Banner: FC<BannerProps> = ({ slice }) => {

  const plainImage = slice.primary.inline_image?.url?.split("?")[0]
  slice.primary.inline_image.url = plainImage

  const ctaText = slice.primary.button_text || "Learn more";
  const ctaDestination = ((slice.primary.link as any)?.url || "").split("?")[0].split("#")[0].split("/").filter(Boolean).pop()?.replace(/-/g, " ");
  const ctaLabel = ctaDestination ? `Learn more about ${ctaDestination}` : ctaText;
  const hiddenSuffix = /^(learn more|read more)$/i.test(ctaText.trim()) && ctaDestination ? ` about ${ctaDestination}` : "";
  
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`font-fellix text-jade ${slice.primary.inset_image_text_under === true ? "" : " max-w-[1280px] my-4"} ${slice.primary.style ? slice.primary.style.toLowerCase() : ""} mx-auto relative`}>
      <div className={`${slice.primary.inset_image_text_under === true ? "pt-14" : " h-screen max-h-[650px] "} relative`}>
        {slice.primary.image ?
          <ResponsivePrismicImage image={slice.primary.image} className="w-full object-cover h-screen max-h-[650px] absolute inset-0 z-0 pointer-events-none" sizes="(max-width: 768px) 80vw, (min-width: 1280px) 1280px, 100vw" priority imgixParams={{ auto: ['compress'], fm: 'avif', q: 8, fit: 'max' }}/>
        : <></>}
        <div className={`${slice.primary.white_text === true ? "text-white" : ""} relative z-10 ${slice.primary.inset_image_text_under === true ? "": " h-screen max-h-[650px]"}`}>
            {slice.primary.inline_image  ?
            <PrismicNextImage className="w-full max-w-[650px] h-auto max-h-[650px] mx-auto object-contain" field={slice.primary.inline_image} fallbackAlt="" sizes="(min-width: 768px) 650px, 100vw" priority imgixParams={{ auto: ['compress'], fm: 'avif', q: 28, fit: 'max' }}/>
            : null} 
            <div className={`${slice.primary.inset_image_text_under === true ? "mx-auto text-center" : ""} p-8 md:p-12 max-w-[700px]`}>
              <article className={`${slice.primary.inset_image_text_under === true ? "text-[28px] leading-[32px] prose prose-h2:!text-[48px] prose-h2:!leading-[56px] mb-4 text-jade"  : ""}`}>
                <PrismicRichText field={slice.primary.headline}/>
              </article>
              <div className={`${slice.primary.text_at_bottom === true ? "absolute bottom-12 left-8 md:left-12" : ""} max-w-[380px] md:max-w-[540px] mx-auto`}>
                <article className="text-[18px] leading-[28px] md:text-[28px] md:leading-[36px] font-normal mt-6">
                  <PrismicRichText field={slice.primary.paragraph}/>
                </article>
                {slice.primary.link?.link_type !=="Any" ?
                <div className={`${slice.primary.inset_image_text_under === true ? "mt-12" : "mt-6"} `}>
                  <PrismicNextLink field={slice.primary.link} aria-label={ctaLabel} className={`py-2 leading-[20px] ${slice.primary.white_text === true ? "!text-white !border-white" : "text-jade border-jade"} button text-[18px]`}>{slice.primary.button_text}{hiddenSuffix ? <span className="sr-only">{hiddenSuffix}</span> : null}</PrismicNextLink>
                </div>
                : <></>}
              </div>
              {slice.primary.fineprint ? 
            <div className="text-[14px] leading-[18px] my-8 max-w-[550px] mx-auto text-center">
              <PrismicRichText field={slice.primary.fineprint}/>
            </div>  : <></>}
            </div>
          </div>
        </div>
    </section>
  );
};

export default Banner;